import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PaddlePrices, SubscriptionService } from 'src/app/shared/services/subscription.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { Plan, PLAN_NAME_KEYS, PlanCode } from 'src/app/shared/models/plan.model';
import { TranslatePipe } from 'src/app/shared/i18n/translate.pipe';
import { SignupComponent } from 'src/app/shared/components/signup/signup.component';
import { I18nService } from 'src/app/shared/i18n/i18n.service';
import { formatBytes } from 'src/app/shared/bytes';

/**
 * Cennik planów abonamentowych. Karty planów przychodzą z backendu
 * (GET /subscription/plans - tabela plan), więc zmiana ceny czy limitu
 * nie wymaga nowego wydania frontendu. Komponent jest osadzany na stronie
 * głównej i na osobnej stronie /pricing.
 *
 * Ceny lokalizowane: kwotę w walucie kupującego podaje Paddle (PricePreview)
 * - kraj wykrywa backend z nagłówków proxy, a bez niego Paddle sam po IP.
 * W razie niepowodzenia karta pokazuje cenę zapasową z tabeli plan
 * (backend trzyma ją zgodną z katalogiem przez webhook price.updated).
 * Cennik sam nic nie sprzedaje: abonament dotyczy konkretnego drzewa,
 * więc zakup odbywa się w Ustawieniach drzewa (sekcja „Abonament”).
 */
@Component({
    selector: 'app-pricing',
    templateUrl: './pricing.component.html',
    styleUrls: ['./pricing.component.css'],
    standalone: true,
    imports: [TranslatePipe]
})
export class PricingComponent implements OnInit {

  plans: Plan[] = [];

  /** Surowe ceny lokalne z Paddle, indeksowane pri_... */
  paddlePrices: PaddlePrices = {};

  /** Trwa pobieranie cen z Paddle - karty pokazują szkielet zamiast cen z bazy */
  pricesLoading = false;

  /**
   * Karty planów są już w drzewie dokumentu. Strona główna czeka na ten sygnał,
   * żeby przewinąć do cennika po wejściu na /pricing - wcześniej nie ma do
   * czego przewijać, bo sekcja renderuje się dopiero z listą planów.
   */
  @Output() loaded = new EventEmitter<void>();

  constructor(private subscriptionService: SubscriptionService,
    private authService: AuthService, private dialog: MatDialog,
    private i18n: I18nService) { }

  ngOnInit() {
    this.subscriptionService.getPlans().subscribe({
      next: (plans) => {
        this.plans = plans ?? [];
        // ceny w walucie klienta prosto z katalogu Paddle;
        // szkielet tylko wtedy, gdy w ogóle jest skąd wziąć cenę; przy
        // niepowodzeniu karta pokazuje cenę z bazy, a nie wieczny placeholder
        this.pricesLoading = this.subscriptionService.canLoadPrices(this.plans);
        void this.subscriptionService.localizedPlanPrices(this.plans)
          .then((prices) => this.paddlePrices = prices)
          .catch(() => this.paddlePrices = {})
          .finally(() => this.pricesLoading = false);
        // sygnał wysyłamy razem z planami, a nie po cenach z Paddle: sekcja
        // jest już wtedy widoczna (ceny dochodzą w miejsce szkieletu), więc
        // przewijanie nie musi czekać na sieć
        if (this.plans.length > 0) {
          this.loaded.emit();
        }
      },
      error: () => this.plans = []
    });
  }

  get loggedIn() {
    return this.authService.isLoggedIn();
  }

  planName(code: string): string {
    return PLAN_NAME_KEYS[code as PlanCode] ?? code;
  }

  /**
   * Cena do pokazania: z Paddle, a gdy Paddle milczy - zapas z tabeli plan.
   * Formatowanie jest wspólne, więc kwota nie zmienia wyglądu, gdy karta
   * przejdzie z jednego źródła na drugie. Pusty string = plan darmowy.
   */
  displayPrice(plan: Plan): string {
    return this.subscriptionService.planPrice(plan, this.paddlePrices, this.i18n.lang);
  }

  /** Plan ma cenę w Paddle, a podgląd jeszcze trwa - pokaż szkielet. */
  priceLoading(plan: Plan): boolean {
    return this.pricesLoading && !!this.priceId(plan);
  }

  /** Limit osób z separatorem tysięcy - „10 000", nie „10000". */
  personLimit(plan: Plan): string {
    return (plan.person_limit ?? 0).toLocaleString(this.i18n.lang);
  }

  /** Roczna cena Paddle planu; pusty string = plan bez ceny w katalogu. */
  priceId(plan: Plan): string {
    return this.subscriptionService.planPriceId(plan);
  }

  formatBytes(bytes: string | null): string {
    return formatBytes(bytes, this.i18n.lang);
  }

  /** Ten sam wzorzec co MainComponent.onSignup(). */
  onSignup(): void {
    this.dialog.open(SignupComponent, {
      data: { inDialog: true },
      disableClose: true,
      autoFocus: false,
      width: '400px',
      maxWidth: '95vw',
    });
  }
}
